import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";

interface GstCalculatorModalProps {
  show: boolean;
  onHide: () => void;
}

interface CalcResult {
  totalAmount: string;
  basicAmount: string;
  cgst: string;
  sgst: string;
  gst: string;
}

// Ports /student/Enrollment/ServiceTaxCalculator.html — a reverse / forward
// 18% GST (CGST 9% + SGST 9%) calculator. Enter EITHER the total (tax-
// inclusive) amount OR the basic amount; the rest is derived.
const MAX_AMOUNT = 1500000;
const round2 = (n: number) => Math.round(n * 100) / 100;

const GstCalculatorModal: React.FC<GstCalculatorModalProps> = ({ show, onHide }) => {
  const [total, setTotal] = useState("");
  const [basic, setBasic] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<CalcResult | null>(null);

  const reset = () => {
    setTotal("");
    setBasic("");
    setError("");
    setResult(null);
  };

  const handleHide = () => {
    reset();
    onHide();
  };

  const calculate = () => {
    setError("");
    setResult(null);

    const xRaw = total.trim();
    const yRaw = basic.trim();
    const x = Number(xRaw) || 0;
    const y = Number(yRaw) || 0;
    const xInvalid = xRaw !== "" && isNaN(Number(xRaw));
    const yInvalid = yRaw !== "" && isNaN(Number(yRaw));

    // Mirrors the original validation rules
    if (
      xInvalid ||
      yInvalid ||
      x > MAX_AMOUNT ||
      y > MAX_AMOUNT ||
      (x === 0 && y === 0) ||
      x < 0 ||
      y < 0
    ) {
      setError("Enter a proper amount");
      return;
    }
    if (x > 0 && y > 0) {
      setError("Enter any one amount only");
      return;
    }

    if (x > 0) {
      // Total (tax-inclusive) given → back out the basic amount
      const basicamt = round2((x / 118) * 100);
      const gst = round2(x - basicamt);
      const cgst = round2(gst / 2);
      const sgst = round2(gst - cgst);
      setResult({
        // Don't round the entered total to a whole number — that turned 199.61
        // into 200. Echo it at 2 decimals like the other rows.
        totalAmount: round2(x).toFixed(2),
        basicAmount: basicamt.toFixed(2),
        cgst: cgst.toFixed(2),
        sgst: sgst.toFixed(2),
        gst: gst.toFixed(2),
      });
    } else {
      // Basic amount given → add 18% GST
      const gst = round2(y * 0.18);
      const cgst = round2(y * 0.09);
      const sgst = round2(gst - cgst);
      const totamt = round2(y + gst);
      setResult({
        totalAmount: totamt.toFixed(2),
        basicAmount: String(y),
        cgst: cgst.toFixed(2),
        sgst: sgst.toFixed(2),
        gst: gst.toFixed(2),
      });
    }
  };

  const rows: { label: string; value: string }[] = result
    ? [
        { label: "Total Amount", value: result.totalAmount },
        { label: "Basic Amount", value: result.basicAmount },
        { label: "CGST @ 9%", value: result.cgst },
        { label: "SGST @ 9%", value: result.sgst },
        { label: "GST @ 18%", value: result.gst },
      ]
    : [];

  return (
    <Modal show={show} onHide={handleHide} centered size="sm" contentClassName="ev-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          <i className="fa fa-calculator" style={{ marginRight: 8 }} />
          GST Calculator
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Total Amount (incl. GST)
          </Form.Label>
          <Form.Control
            type="number"
            value={total}
            placeholder="Enter total amount"
            disabled={basic.trim() !== ""}
            onChange={(e) => setTotal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && calculate()}
          />
        </Form.Group>

        <div
          style={{
            textAlign: "center",
            fontSize: 11,
            color: "var(--text-faint)",
            margin: "2px 0 6px",
            fontWeight: 600,
          }}
        >
          — OR —
        </div>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Basic Amount (excl. GST)
          </Form.Label>
          <Form.Control
            type="number"
            value={basic}
            placeholder="Enter basic amount"
            disabled={total.trim() !== ""}
            onChange={(e) => setBasic(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && calculate()}
          />
        </Form.Group>

        {error && (
          <div style={{ color: "var(--status-danger)", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <Button variant="primary" size="sm" onClick={calculate}>
            Calculate
          </Button>
          <Button variant="secondary" size="sm" onClick={reset}>
            Reset
          </Button>
        </div>

        {result && (
          <div className="ev-gst-result">
            {rows.map((r) => (
              <div key={r.label} className="ev-gst-result-row">
                <span className="ev-gst-result-label">{r.label}</span>
                <span className="ev-gst-result-value">{r.value}</span>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default GstCalculatorModal;
